import { useEffect, useState } from 'react';
import { Alert, Badge, Button, Group, Loader, Stack, Table, Text, Center } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconArrowRight, IconCheck, IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import { checkDealStatus, recalcDealStatus } from '@ayocore/grasias/client/rpc';
import logger from '@ayocore/platform/common/helpers/logger';

type DealStatusCheck = Awaited<ReturnType<typeof checkDealStatus>>;

const money = (v: number | null | undefined): string =>
  v == null ? '—' : new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 2 }).format(Number(v));

const yesNo = (v: boolean): string => (v ? 'Да' : 'Нет');

/**
 * Модалка диагностики статуса сделки (read-only).
 * Показывает, какой статус должен быть по правилам Grasias и почему.
 * Ничего не меняет — только проверка.
 */
export function CheckDealStatus({
  dealId,
  onReload,
}: {
  dealId: number;
  /** Перезагрузить карточку сделки после применённого пересчёта. */
  onReload?: () => void;
}) {
  const [data, setData] = useState<DealStatusCheck | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [recalculating, setRecalculating] = useState(false);

  const handleRecalc = async () => {
    if (recalculating || !data) return;
    setRecalculating(true);
    const prevStatus = data.current_status;
    try {
      const res = await recalcDealStatus(dealId);
      setData(res);
      onReload?.();
      const changed = prevStatus !== res.current_status;
      notifications.show({
        color: changed ? 'teal' : 'gray',
        message: changed
          ? `Статус изменён на «${res.current_status_name}»`
          : 'Статус не изменился',
      });
    } catch (err) {
      logger.error('[grasias] recalcDealStatus error', err);
      notifications.show({
        color: 'red',
        message: err instanceof Error ? err.message : 'Не удалось пересчитать статус',
      });
    } finally {
      setRecalculating(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    checkDealStatus(dealId)
      .then(res => {
        if (!cancelled) setData(res);
      })
      .catch(err => {
        if (cancelled) return;
        logger.error('[grasias] checkDealStatus error', err);
        setError(err instanceof Error ? err.message : 'Не удалось проверить статус');
      });
    return () => {
      cancelled = true;
    };
  }, [dealId]);

  if (error) {
    return (
      <Alert color='red' title='Ошибка'>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Center py='lg'>
        <Loader size='sm' />
      </Center>
    );
  }

  return (
    <Stack gap='sm'>
      {/* Текущий → целевой статус */}
      <Group gap='xs' align='center' wrap='nowrap'>
        <Badge variant='light' color='gray' size='lg' style={{ textTransform: 'none' }}>
          {data.current_status_name ?? data.current_status ?? '—'}
        </Badge>
        <IconArrowRight size={16} />
        <Badge
          variant='light'
          color={data.would_change ? 'orange' : 'teal'}
          size='lg'
          style={{ textTransform: 'none' }}
        >
          {data.target_status_name ?? data.target_status ?? '—'}
        </Badge>
        {data.would_change ? (
          <Group gap={4} c='orange'>
            <IconAlertTriangle size={16} />
            <Text size='sm' fw={600}>
              Отличается
            </Text>
          </Group>
        ) : (
          <Group gap={4} c='teal'>
            <IconCheck size={16} />
            <Text size='sm' fw={600}>
              Соответствует
            </Text>
          </Group>
        )}
      </Group>

      {/* Причина */}
      <Alert color={data.would_change ? 'orange' : 'teal'} variant='light'>
        {data.reason}
      </Alert>

      {/* Метрики, на которых построено решение */}
      <Table withRowBorders={false} verticalSpacing={4} fz='sm'>
        <Table.Tbody>
          <Table.Tr>
            <Table.Td c='dimmed'>Сумма сделки</Table.Td>
            <Table.Td ta='right'>{money(data.deal_amount)} ₽</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td c='dimmed'>Оплата (PAY_DOC)</Table.Td>
            <Table.Td ta='right'>{money(data.pay_sum)} ₽</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td c='dimmed'>Реализация (SELL_DOC)</Table.Td>
            <Table.Td ta='right'>{money(data.sell_sum)} ₽</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td c='dimmed'>Порог «полной» (допуск {money(data.tolerance)} ₽)</Table.Td>
            <Table.Td ta='right'>{money(data.threshold)} ₽</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td c='dimmed'>Есть документы 1С</Table.Td>
            <Table.Td ta='right'>{yesNo(data.has_docs)}</Table.Td>
          </Table.Tr>
          <Table.Tr>
            <Table.Td c='dimmed'>Есть расходы</Table.Td>
            <Table.Td ta='right'>{yesNo(data.has_expenses)}</Table.Td>
          </Table.Tr>
          {(data.archived || data.protected) && (
            <Table.Tr>
              <Table.Td c='dimmed'>Пересчёт заблокирован</Table.Td>
              <Table.Td ta='right'>
                {data.archived ? 'Сделка в архиве' : 'Статус защищён (закрыт/отменён)'}
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>

      <Group justify='space-between' align='center' mt='xs'>
        <Text size='xs' c='dimmed'>
          «Пересчитать статус» применяет те же правила, что и автоматический пересчёт при
          изменении документов и строк.
        </Text>
        <Button
          size='xs'
          variant='filled'
          leftSection={<IconRefresh size={14} />}
          loading={recalculating}
          onClick={handleRecalc}
        >
          Пересчитать статус
        </Button>
      </Group>
    </Stack>
  );
}

export default CheckDealStatus;
